import _ from "lodash";
import moment from "moment";
import { db, docFromSnapshot, docsFromSnapshot } from "@/firebase";
import { functions } from "@/firebase";
import { BOOKING } from "@/helpers/eventTypes";
import {
  getServiceResourceIds,
  mergeBookingsAvailability,
  sortAvailabilityByDate,
} from "@/utils/staleRefGuards";
import {
  BOOKING_SEARCH_LOOKBACK_MONTHS,
  rankBookingSearch,
  withSearchLookups
} from "@/helpers/bookingSearch";

const createBookingCustomer = functions.httpsCallable("createBookingCustomer");
const createBookingAdmin = functions.httpsCallable("createBookingAdmin");
const updateBookingAdmin = functions.httpsCallable("updateBookingAdmin");

export const bookingEventTypes = {
  BOOKING
};

export const bookingStatusTypes = {
  UNCONFIRMED: "UNCONFIRMED",
  CONFIRMED: "CONFIRMED",
  CANCELLED: "CANCELLED"
};

export const paymentStatusTypes = {
  NOT_PAID: "NOT_PAID",
  PAID: "PAID"
};

export const DEFAULT_BOOKING = {
  type: bookingEventTypes.BOOKING,
  tenantId: null,
  storeId: null,
  bookingId: null,
  resourceId: null,
  serviceId: null,
  servicePackageId: null,
  start: null,
  end: null,
  startDate: null,
  endDate: null,
  customer: {
    name: null,
    emailAddress: null,
    phoneNumber: null
  },
  notes: null,
  total: null,
  createdByAdmin: false,
  bookingStatus: bookingStatusTypes.UNCONFIRMED,
  paymentStatus: paymentStatusTypes.NOT_PAID
};

export const DEFAULT_ADMIN_BOOKING = {
  ...DEFAULT_BOOKING,
  createdByAdmin: true,
  bookingStatus: bookingStatusTypes.CONFIRMED
};

const SET_BOOKINGS = "SET_BOOKINGS";
const SET_BOOKINGS_SUBSCRIPTION = "SET_BOOKINGS_SUBSCRIPTION";
const RESET_AVAILABLE_BOOKING_TIMES = "RESET_AVAILABLE_BOOKING_TIMES";
const SET_AVAILABLE_BOOKING_TIMES = "SET_AVAILABLE_BOOKING_TIMES";

const state = {
  bookings: {},
  bookingsSubscription: null,
  availableTimes: {}
};

const getters = {
  bookings: state => state.bookings,
  bookingsList: state => Object.values(state.bookings),
  bookingsConfirmed: state =>
    _.filter(
      state.bookings,
      booking => booking.bookingStatus !== "UNCONFIRMED"
    ),
  bookingById: state => id => state.bookings[id],
  availableDates: state => Object.keys(state.availableTimes),
  availableTimesByDate: state => date => state.availableTimes[date] || []
};

async function getBookingsAvailability(resource, service) {
  const snapshot = await db
    .collection("availableBookings")
    .doc(`${resource}-${service}`)
    .get();
  const data = docFromSnapshot(snapshot)
  return data ? data.bookings : data;
}

const SEARCH_CACHE_TTL_MS = 60 * 1000;

const searchCache = {
  storeId: null,
  fetchedAt: 0,
  records: [],
  inflight: null
};

export function resetBookingSearchCache() {
  searchCache.storeId = null;
  searchCache.fetchedAt = 0;
  searchCache.records = [];
  searchCache.inflight = null;
}

export function isSearchCacheReady(storeId) {
  if (!storeId || searchCache.storeId !== storeId) return false;
  if (searchCache.inflight) return true;
  return searchCache.fetchedAt > 0;
}

function mapSearchSnapshot(snapshot) {
  const result = [];
  snapshot.forEach(doc => {
    result.push(formatBookingsForSearchTable(doc));
  });
  return result;
}

async function fetchSearchableBookings(storeId) {
  const since = moment
    .utc()
    .subtract(BOOKING_SEARCH_LOOKBACK_MONTHS, "months")
    .startOf("day")
    .toISOString();

  try {
    const snapshot = await db
      .collection("bookings")
      .where("storeId", "==", storeId)
      .where("start", ">=", since)
      .get();
    return mapSearchSnapshot(snapshot);
  } catch (error) {
    const snapshot = await db
      .collection("bookings")
      .where("storeId", "==", storeId)
      .get();
    return mapSearchSnapshot(snapshot);
  }
}

async function loadSearchableBookings(storeId) {
  const now = Date.now();
  if (
    searchCache.storeId === storeId &&
    searchCache.fetchedAt &&
    now - searchCache.fetchedAt < SEARCH_CACHE_TTL_MS
  ) {
    return searchCache.records;
  }
  if (searchCache.inflight && searchCache.storeId === storeId) {
    return searchCache.inflight;
  }

  searchCache.storeId = storeId;
  searchCache.inflight = fetchSearchableBookings(storeId)
    .then(records => {
      searchCache.records = records;
      searchCache.fetchedAt = Date.now();
      searchCache.inflight = null;
      return records;
    })
    .catch(error => {
      searchCache.inflight = null;
      throw error;
    });

  return searchCache.inflight;
}

export function prefetchSearchableBookings(storeId) {
  if (!storeId) return Promise.resolve([]);
  return loadSearchableBookings(storeId).catch(() => []);
}

/**
 * Modern booking search: load recent store bookings once, then rank
 * locally (partial name, last name, typos, Irish phones, email, staff).
 */
export async function searchStoreBookings(query, storeId, options = {}) {
  const records = await loadSearchableBookings(storeId);
  const enriched = withSearchLookups(records, options.lookups || {});
  return rankBookingSearch(enriched, query, options);
}

/** @deprecated Use searchStoreBookings. Kept for existing call sites. */
async function getBookingsByField(query, storeId, field, options = {}) {
  return searchStoreBookings(query, storeId, {
    ...options,
    preferField: field || options.preferField
  });
}

function formatBookingsForSearchTable(doc) {
  const data = (doc && typeof doc.data === "function" ? doc.data() : doc) || {};
  const customer = data.customer || {};
  const start = data.start;
  const startMoment = start ? moment.utc(start) : null;
  const validStart = startMoment && startMoment.isValid() ? startMoment : null;
  return {
    _id: (doc && doc.id) || data._id || "",
    date: validStart ? validStart.format("DD/MM/YYYY") : "",
    startDate: validStart
      ? validStart.format("YYYY-MM-DD")
      : data.startDate || "",
    startMs: validStart ? validStart.valueOf() : 0,
    email: customer.emailAddress || "",
    name: customer.name || "",
    number: customer.phoneNumber || "",
    time: validStart ? validStart.format("HH:mm") : "",
    notes: data.notes || "",
    bookingStatus: data.bookingStatus || "",
    paymentStatus: data.paymentStatus || "",
    resourceId: data.resourceId || "",
    serviceId: data.serviceId || "",
    servicePackageId: data.servicePackageId || ""
  };
}

async function getStoreAgg(shopId) {
  const snapshot = await db
    .collection("aggregations")
    .doc(shopId)
    .get();
  return docFromSnapshot(snapshot);
}

const actions = {
  subscribeBookings(context, options) {
    if (context.state.bookingsSubscription) {
      context.dispatch('unsubscribeBookings');
    }

    const subscription = db
      .collection("bookings")
      .where("storeId", "==", context.rootGetters.userClaims.storeId)
      .where("start", ">=", options.dateFrom)
      .where("start", "<", options.dateTo)
      .onSnapshot(snap => {
        const bookings = docsFromSnapshot(snap);

        context.commit(SET_BOOKINGS, bookings);
      });

    context.commit(SET_BOOKINGS_SUBSCRIPTION, subscription);
  },
  async fetchBookings(context, options) {
    const snap = await db
      .collection("bookings")
      .where("storeId", "==", context.rootGetters.userClaims.storeId)
      .where("start", ">=", options.dateFrom)
      .where("start", "<", options.dateTo)
      .get();

    const docs = docsFromSnapshot(snap);

    context.commit(SET_BOOKINGS, docs);
  },
  unsubscribeBookings(context) {
    const unsubscribe = context.state.bookingsSubscription;

    if (unsubscribe) {
      unsubscribe();
      context.commit(SET_BOOKINGS_SUBSCRIPTION, null);
    }
  },
  async createCustomerBooking(context, booking) {
    booking.created = +new Date();
    booking.timestamp = booking.created;

    return createBookingCustomer({ booking });
  },
  createAdminBooking(context, { booking, override = false }) {
    booking = {
      ...booking,
      tenantId: context.rootGetters.tenantId,
      storeId: context.rootGetters.userClaims.storeId,
      lastModified: +new Date(),
      created: +new Date()
    };

    return createBookingAdmin({ booking, override });
  },
  updateBooking(context, { booking, override = false }) {
    booking.lastModified = +new Date();
    return updateBookingAdmin({ booking, override });
  },
  markBookingAsPaid(context, booking) {
    return context.dispatch("updateBooking", {
      booking: {
        ...booking,
        paymentStatus: paymentStatusTypes.PAID
      }
    });
  },
  confirmBooking(context, booking) {
    return context.dispatch("updateBooking", {
      booking: {
        ...booking,
        bookingStatus: bookingStatusTypes.CONFIRMED
      }
    });
  },
  cancelBooking(context, booking) {
    return context.dispatch("updateBooking", {
      booking: {
        ...booking,
        bookingStatus: bookingStatusTypes.CANCELLED
      }
    });
  },
  async fetchAvailableTimes(context, { resourceId, serviceId }) {
    context.commit(RESET_AVAILABLE_BOOKING_TIMES);
    let availableBookingTimes;
    if (resourceId === "Any") {
      const { services } = await getStoreAgg(context.rootGetters.userClaims.storeId);
      const resources = getServiceResourceIds(services, serviceId);
      const arr = [];
      for (const resource of resources) {
        const bookings = await getBookingsAvailability(resource, serviceId);
        if (bookings) {
          arr.push(bookings);
        }
      }

      availableBookingTimes = mergeBookingsAvailability(arr);
    } else {
      availableBookingTimes = await getBookingsAvailability(resourceId, serviceId);
    }
    context.commit(
      SET_AVAILABLE_BOOKING_TIMES,
      sortAvailabilityByDate(availableBookingTimes || {})
    );
  }
};

const mutations = {
  [SET_BOOKINGS](state, bookings) {
    state.bookings = bookings;
  },
  [SET_BOOKINGS_SUBSCRIPTION](state, bookingsSubscription) {
    state.bookingsSubscription = bookingsSubscription;
  },
  [RESET_AVAILABLE_BOOKING_TIMES](state) {
    state.availableTimes = {};
  },
  [SET_AVAILABLE_BOOKING_TIMES](state, availableTimes) {
    state.availableTimes = availableTimes;
  }
};

export default {
  state,
  getters,
  actions,
  mutations,
  getBookingsAvailability,
  getBookingsByField,
  searchStoreBookings,
  prefetchSearchableBookings,
  resetBookingSearchCache,
  isSearchCacheReady,
  namespaced: true
};
